import type {
  CallingHours,
  CallingWeekday,
} from "@repo/app-types/entities/teammateAgent";
import { DateTime } from "luxon";

/** A day's calling window as stored: `[start, end)` in "HH:mm". */
export type CallingWindow = readonly [string, string];

/** What a day resets to when it's turned back on. */
export const DEFAULT_CALLING_WINDOW: CallingWindow = ["09:00", "17:00"];

/**
 * The window shown for a weekday, or undefined when the agent doesn't call
 * that day.
 *
 * `CallingHours` allows several windows per day (a lunch break, say) but this
 * editor models one, so it reads and writes the first. Nothing else writes
 * these settings today, so a second window can only arrive from a direct
 * database edit.
 */
export function getCallingWindow(
  hours: CallingHours | undefined,
  weekday: CallingWeekday,
): CallingWindow | undefined {
  return hours?.[weekday]?.[0];
}

export function setCallingDayEnabled(
  hours: CallingHours | undefined,
  weekday: CallingWeekday,
  isEnabled: boolean,
): CallingHours {
  return {
    ...hours,
    [weekday]: isEnabled ? [DEFAULT_CALLING_WINDOW] : [],
  };
}

/** Time-of-day granularity offered by `TimeSelect`. */
const SLOT_MINUTES = 15;

/** The last window a day can hold, given windows are half-open and same-day. */
const LAST_SLOT = "23:45";

/** The latest a window can start and still have room to end. */
const SECOND_TO_LAST_SLOT = "23:30";

/** The slot `SLOT_MINUTES` after `time`, or undefined past the end of the day. */
function nextSlot(time: string): string | undefined {
  const [hour = 0, minute = 0] = time.split(":").map(Number);
  const total = hour * 60 + minute + SLOT_MINUTES;
  if (total > 23 * 60 + 45) {
    return undefined;
  }
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * The earliest end time offerable for a window starting at `start`. Windows are
 * `[start, end)` and `callingWindowSchema` requires start < end, so an end
 * equal to the start has to be off the menu — `TimeSelect`'s `minTime` is
 * inclusive.
 */
export function minimumEndTime(start: string): string {
  return nextSlot(start) ?? LAST_SLOT;
}

/**
 * Moving one bound drags the other along rather than letting the window invert:
 * `callingWindowSchema` rejects start >= end, and a rejected payload would fail
 * the whole config save, not just this field.
 */
export function setCallingWindowTime(
  hours: CallingHours | undefined,
  weekday: CallingWeekday,
  bound: "start" | "end",
  time: string,
): CallingHours {
  const window = getCallingWindow(hours, weekday);
  if (!window) {
    return { ...hours };
  }

  const [start, end] = window;

  let nextWindow: CallingWindow;
  if (bound === "start") {
    // A start in the final slot leaves no room for an end after it, so it
    // backs off by one rather than producing a zero-length window.
    const nextStart = time < LAST_SLOT ? time : SECOND_TO_LAST_SLOT;
    nextWindow = [nextStart, end > nextStart ? end : minimumEndTime(nextStart)];
  } else {
    nextWindow = [start, time > start ? time : minimumEndTime(start)];
  }

  return { ...hours, [weekday]: [nextWindow] };
}

/**
 * The short zone name for the agent's calling timezone (e.g. "EDT"), shown
 * beside each window so it's clear the times aren't the viewer's local ones.
 */
export function getTimezoneAbbreviation(timezone: string): string {
  const zoned = DateTime.now().setZone(timezone);
  return zoned.isValid ? zoned.toFormat("ZZZZ") : timezone;
}
